import { jsPDF } from 'jspdf'

// Force a browser-renderable, modestly-sized JPG so a photo embeds reliably in
// the PDF (also sidesteps HEIC). Best-effort: a photo that still fails to load
// is skipped, never breaking the download.
const cloudinaryForPdf = (url) => {
  if (typeof url !== 'string') return url
  return url.includes('/upload/')
    ? url.replace('/upload/', '/upload/w_700,f_jpg,q_auto/')
    : url
}

// Load an image URL to a data URL via canvas. Resolves null on any failure
// (network / cross-origin taint) so the caller can simply skip it.
const loadImage = (url) =>
  new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = img.naturalWidth
        canvas.height = img.naturalHeight
        canvas.getContext('2d').drawImage(img, 0, 0)
        resolve({
          dataUrl: canvas.toDataURL('image/jpeg', 0.82),
          w: img.naturalWidth,
          h: img.naturalHeight
        })
      } catch (e) {
        resolve(null)
      }
    }
    img.onerror = () => resolve(null)
    img.src = cloudinaryForPdf(url)
  })

const slug = (s) => (s || '').replace(/[^\w-]+/g, '_').replace(/^_+|_+$/g, '')

/**
 * Build + download a term report PDF.
 *
 * report = {
 *   studentName, centreName, level, termLabel, published,
 *   skills: [{ name, los: [{ name, rating }], note: { text, image_urls } }]
 * }
 *
 * Photos are embedded best-effort — one that fails to load is skipped silently.
 */
export const downloadTermReportPdf = async (report) => {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' })
  const pageW = doc.internal.pageSize.getWidth()
  const pageH = doc.internal.pageSize.getHeight()
  const margin = 40
  const contentW = pageW - margin * 2
  let y = margin

  const ensureSpace = (h) => {
    if (y + h > pageH - margin) {
      doc.addPage()
      y = margin
    }
  }

  // ---- header ----
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(16)
  doc.text('Term Report', margin, y)
  y += 22
  doc.setFontSize(13)
  doc.text(report.studentName || '', margin, y)
  y += 18
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.setTextColor(90)
  const meta = [
    report.centreName && `Centre: ${report.centreName}`,
    report.level && `Level: ${report.level}`,
    report.termLabel,
    report.published ? 'Published' : 'Draft'
  ]
    .filter(Boolean)
    .join('   •   ')
  doc.text(meta, margin, y)
  y += 16
  doc.setTextColor(0)
  doc.setDrawColor(220)
  doc.line(margin, y, pageW - margin, y)
  y += 18

  // ---- per skill ----
  for (const skill of report.skills || []) {
    ensureSpace(30)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(12)
    doc.text(skill.name || '', margin, y)
    y += 16

    doc.setFont('helvetica', 'normal')
    doc.setFontSize(10)
    for (const lo of skill.los || []) {
      const line = `•  ${lo.name}:  ${lo.rating || 'NA'}`
      const wrapped = doc.splitTextToSize(line, contentW)
      ensureSpace(wrapped.length * 13)
      doc.text(wrapped, margin, y)
      y += wrapped.length * 13 + 2
    }

    if (skill.note && skill.note.text) {
      const noteLines = doc.splitTextToSize(
        `Notes: ${skill.note.text}`,
        contentW
      )
      ensureSpace(noteLines.length * 12 + 4)
      doc.setTextColor(70)
      doc.text(noteLines, margin, y)
      doc.setTextColor(0)
      y += noteLines.length * 12 + 6
    }

    const urls = (skill.note && skill.note.image_urls) || []
    if (urls.length) {
      const imgs = await Promise.all(urls.map(loadImage))
      const thumb = 90
      const gap = 8
      let x = margin
      ensureSpace(thumb + 10)
      for (const im of imgs) {
        if (!im) continue
        if (x + thumb > pageW - margin) {
          x = margin
          y += thumb + gap
          ensureSpace(thumb + 10)
        }
        const ratio = im.w && im.h ? im.h / im.w : 1
        const h = Math.min(thumb, Math.max(24, thumb * ratio))
        try {
          doc.addImage(im.dataUrl, 'JPEG', x, y, thumb, h)
        } catch (e) {
          // a single bad image must never break the whole file
        }
        x += thumb + gap
      }
      y += thumb + 12
    }

    y += 8
  }

  doc.save(
    `${slug(report.studentName) || 'term-report'}_${
      slug(report.termLabel) || 'term'
    }.pdf`
  )
}
